/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { LangGraphConversationRequest } from '../models/lang-graph-conversation-request';

@Injectable({
  providedIn: 'root',
})
export class WorkflowApiService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation langGraphConversation
   */
  static readonly LangGraphConversationPath = '/langgraph/conversation';

  /**
   * Execute LangGraph workflow with NDJSON streaming.
   *
   * Executes a LangGraph workflow with streaming updates through multiple steps:
   * 1. **Routing** - Determine query topic
   * 2. **Product Matching** - Find relevant products (may pause for HITL)
   * 3. **Query Rewriting** - Optimize search query
   * 4. **Search** - Search knowledge base with reranking
   * 5. **Response Generation** - Generate final answer
   *
   * ## Response Format: NDJSON Streaming
   *
   * Each line is a separate JSON object. The `X-Thread-ID` header contains the thread ID.
   *
   * **Update Types:**
   * - `thread_id` - Thread ID for future requests (first update)
   * - `status` - Workflow status messages
   * - `intermediate_state` - Workflow step progress (multiple updates)
   * - `heartbeat` - Keepalive for long-running workflows
   * - `waiting_for_input` - Paused, needs user selection (resume required)
   * - `final_state` - Workflow completed with final response
   * - `error` - Workflow error occurred
   *
   * ## Client Implementation
   *
   * **Stream Processing** (`langgraph_client.py` lines 116-242):
   * ```python
   * async for update in client.langgraph_conversation(messages, thread_id, user_input):
   *     update_type = update.get("type")
   *     
   *     if update_type == "thread_id":
   *         self.thread_id = update.get("thread_id")
   *     
   *     elif update_type == "intermediate_state":
   *         # Merge workflow state deltas
   *         workflow_state.update(update.get("data", {}).get("workflow_state", {}))
   *     
   *     elif update_type == "waiting_for_input":
   *         # Show product selection dialog, then resume with user_input
   *         
   *     elif update_type == "final_state":
   *         # Extract final response
   *         final_response = workflow_state["response_generation"]["response"]
   * ```
   *
   * ## Two Request Modes
   *
   * ### 1. Initial Request (New or Continue)
   * Send messages with optional thread_id. Omit thread_id to start new conversation.
   *
   * ### 2. Resume Request (After HITL Pause)
   * When you receive `waiting_for_input`, call again with:
   * - **Same** `thread_id` from X-Thread-ID header
   * - **Same** `messages` array
   * - **New** `user_input` parameter with user's selection
   *
   * ## Human-in-the-Loop (HITL) Flow
   *
   * **Pause** (`waiting_for_input` update):
   * ```json
   * {
   *   "type": "waiting_for_input",
   *   "input_request": {
   *     "type": "product_selection",
   *     "options": ["SM58", "SM7B", "SM57"],
   *     "prompt": "Which product would you like to know more about?",
   *     "query": "Tell me about microphones",
   *     "reasoning": "Multiple products found, need clarification"
   *   }
   * }
   * ```
   *
   * **Resume** (call this endpoint again with `user_input`):
   * ```json
   * {
   *   "messages": [{"role": "user", "content": "Tell me about microphones"}],
   *   "thread_id": "e74b3c9d",
   *   "user_input": {
   *     "confirmed_product": "SM58",
   *     "human_feedback": "confirmed"
   *   }
   * }
   * ```
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `langGraphConversation()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  langGraphConversation$Response(params: {
    context?: HttpContext
    body: LangGraphConversationRequest
  }
): Observable<StrictHttpResponse<Blob>> {

    const rb = new RequestBuilder(this.rootUrl, WorkflowApiService.LangGraphConversationPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: 'application/x-ndjson',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Blob>;
      })
    );
  }

  /**
   * Execute LangGraph workflow with NDJSON streaming.
   *
   * Executes a LangGraph workflow with streaming updates through multiple steps:
   * 1. **Routing** - Determine query topic
   * 2. **Product Matching** - Find relevant products (may pause for HITL)
   * 3. **Query Rewriting** - Optimize search query
   * 4. **Search** - Search knowledge base with reranking
   * 5. **Response Generation** - Generate final answer
   *
   * ## Response Format: NDJSON Streaming
   *
   * Each line is a separate JSON object. The `X-Thread-ID` header contains the thread ID.
   *
   * **Update Types:**
   * - `thread_id` - Thread ID for future requests (first update)
   * - `status` - Workflow status messages
   * - `intermediate_state` - Workflow step progress (multiple updates)
   * - `heartbeat` - Keepalive for long-running workflows
   * - `waiting_for_input` - Paused, needs user selection (resume required)
   * - `final_state` - Workflow completed with final response
   * - `error` - Workflow error occurred
   *
   * ## Client Implementation
   *
   * **Stream Processing** (`langgraph_client.py` lines 116-242):
   * ```python
   * async for update in client.langgraph_conversation(messages, thread_id, user_input):
   *     update_type = update.get("type")
   *     
   *     if update_type == "thread_id":
   *         self.thread_id = update.get("thread_id")
   *     
   *     elif update_type == "intermediate_state":
   *         # Merge workflow state deltas
   *         workflow_state.update(update.get("data", {}).get("workflow_state", {}))
   *     
   *     elif update_type == "waiting_for_input":
   *         # Show product selection dialog, then resume with user_input
   *         
   *     elif update_type == "final_state":
   *         # Extract final response
   *         final_response = workflow_state["response_generation"]["response"]
   * ```
   *
   * ## Two Request Modes
   *
   * ### 1. Initial Request (New or Continue)
   * Send messages with optional thread_id. Omit thread_id to start new conversation.
   *
   * ### 2. Resume Request (After HITL Pause)
   * When you receive `waiting_for_input`, call again with:
   * - **Same** `thread_id` from X-Thread-ID header
   * - **Same** `messages` array
   * - **New** `user_input` parameter with user's selection
   *
   * ## Human-in-the-Loop (HITL) Flow
   *
   * **Pause** (`waiting_for_input` update):
   * ```json
   * {
   *   "type": "waiting_for_input",
   *   "input_request": {
   *     "type": "product_selection",
   *     "options": ["SM58", "SM7B", "SM57"],
   *     "prompt": "Which product would you like to know more about?",
   *     "query": "Tell me about microphones",
   *     "reasoning": "Multiple products found, need clarification"
   *   }
   * }
   * ```
   *
   * **Resume** (call this endpoint again with `user_input`):
   * ```json
   * {
   *   "messages": [{"role": "user", "content": "Tell me about microphones"}],
   *   "thread_id": "e74b3c9d",
   *   "user_input": {
   *     "confirmed_product": "SM58",
   *     "human_feedback": "confirmed"
   *   }
   * }
   * ```
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `langGraphConversation$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  langGraphConversation(params: {
    context?: HttpContext
    body: LangGraphConversationRequest
  }
): Observable<Blob> {

    return this.langGraphConversation$Response(params).pipe(
      map((r: StrictHttpResponse<Blob>) => r.body as Blob)
    );
  }

}
